import { useCallback, useEffect, useState } from "react";
import { X, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export function useAsync(fn, deps) {
  const [state, setState] = useState({ data: null, loading: true, error: "" });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let on = true;
    setState((s) => ({ ...s, loading: true, error: "" }));
    fn()
      .then((data) => on && setState({ data, loading: false, error: "" }))
      .catch((e) => on && setState({ data: null, loading: false, error: formatApiError(e) }));
    return () => {
      on = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);
  return { ...state, reload: () => setTick((t) => t + 1) };
}

export const Loading = () => (
  <p className="ap-empty" data-testid="ap-loading">
    <Loader2 size={18} className="animate-spin inline-block mr-2" /> Loading…
  </p>
);

export const ErrorBox = ({ error }) => <div className="ap-error" data-testid="ap-error">{error}</div>;

export const Empty = ({ children = "Nothing here yet." }) => <div className="ap-empty">{children}</div>;

const TONES = {
  approved: "green", paid: "green", active: "green", verified: "green", open: "green",
  mutual: "green", meeting_planned: "gold", successful_match: "gold",
  pending: "amber", pending_admin_review: "amber", under_review: "amber", fee_waived: "gold", agreed: "amber",
  pending_verification: "amber",
  correction_requested: "amber", incomplete: "gray", submitted: "gray",
  rejected: "red", blocked: "red", failed: "red", new: "amber", suspended: "red", declined: "red",
  deactivated: "gray", closed: "gray", dismissed: "gray", not_verified: "gray",
};

export const Chip = ({ value, children }) => (
  <span className={`ap-chip ap-chip-${TONES[String(value).toLowerCase()] || "gray"}`}>{children || String(value)}</span>
);

export const StatCard = ({ label, value, testId }) => (
  <div className="ap-stat" data-testid={testId || `ap-stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <div className="ap-stat-value">{value}</div>
    <div className="ap-stat-label">{label}</div>
  </div>
);

export const Modal = ({ title, onClose, children, wide }) => (
  <div className="ap-modal-overlay" onClick={onClose}>
    <div className={`ap-modal${wide ? " ap-modal-wide" : ""}`} onClick={(e) => e.stopPropagation()}>
      <div className="ap-modal-head">
        <h3>{title}</h3>
        <button type="button" className="btn btn-outline btn-sm" onClick={onClose} aria-label="Close">
          <X size={14} />
        </button>
      </div>
      {children}
    </div>
  </div>
);

export const Field = ({ label, children }) => (
  <div className="ap-field">
    <label className="ap-label">{label}</label>
    {children}
  </div>
);

export const Table = ({ columns, children }) => (
  <div style={{ overflowX: "auto" }}>
    <table className="ap-table">
      <thead>
        <tr>{columns.map((c) => <th key={c}>{c}</th>)}</tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

export const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
  } catch (e) {
    return iso;
  }
};

export const fmtMoney = (v) => (v === "" || v == null ? "—" : `₹${v}`);

export function useAction() {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const run = useCallback(async (fn, after) => {
    setBusy(true);
    setErr("");
    try {
      const r = await fn();
      if (after) after(r);
      return r;
    } catch (e) {
      setErr(formatApiError(e));
      return null;
    } finally {
      setBusy(false);
    }
  }, []);
  return { busy, err, run, setErr };
}

export const NOTIFICATION_TYPES = [
  "interest_approved", "interest_received", "interest_accepted", "profile_approved",
  "profile_rejected", "payment_status", "meeting_update", "discussion_update", "general",
];

export const MATCH_STATUS_OPTIONS = [
  "mutual_interest", "pending_contact", "contacted", "meeting_planned",
  "meeting_completed", "follow_up_required", "closed", "successful_match",
];

export const SIDE_PAYMENT_OPTIONS = ["pending", "agreed", "partially_paid", "paid", "received", "waived"];
